/**
 * Button — the one action element (design-system-lld.md §4.4).
 *
 * Intent variants (primary/secondary/ghost/danger/dangerGhost) replace the
 * old shadcn theme (default/destructive/outline/secondary/ghost/link).
 *
 * Backward-compat alias map (documented, do not remove without a migration
 * pass — 14+ existing files still pass the old variant names):
 *   "default"     -> "primary"
 *   "outline"     -> "secondary"
 *   "destructive" -> "danger"
 *   "secondary"   -> "secondary"   (name unchanged, restyled to tokens)
 *   "ghost"       -> "ghost"       (name unchanged, restyled to tokens)
 *   "link"        -> "ghost"       (no dedicated link treatment in the new kit)
 *
 * Old `size="lg"` / `size="default"` / `size="icon"` are not used by any
 * current Button caller (verified by grep across src/) so the new kit only
 * ships sm/md/icon/iconSm per the LLD ("no lg — nothing in the console
 * needs it") without needing a size alias.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const LEGACY_VARIANT_ALIAS = {
  default: 'primary',
  destructive: 'danger',
  outline: 'secondary',
  secondary: 'secondary',
  ghost: 'ghost',
  link: 'ghost',
} as const;

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerGhost';
export type LegacyButtonVariant = keyof typeof LEGACY_VARIANT_ALIAS;
export type ButtonSize = 'sm' | 'md' | 'icon' | 'iconSm';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
        secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400',
        ghost: 'bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900',
        danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
        dangerGhost: 'bg-transparent text-red-600 hover:bg-red-50',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-9 px-4 text-sm',
        icon: 'h-9 w-9 p-0',
        iconSm: 'h-8 w-8 p-0',
      },
    },
    defaultVariants: {
      // Matches the old shadcn `default` (solid blue) fallback so the ~114
      // call sites that omit `variant` entirely keep their prior look —
      // the LLD's "secondary is everything else" guidance is a per-call
      // authoring convention, not a safe default for un-migrated callers.
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    Omit<VariantProps<typeof buttonVariants>, 'variant' | 'size'> {
  variant?: ButtonVariant | LegacyButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  /** Shows an inline spinner and disables the button while true. */
  isLoading?: boolean;
  children?: React.ReactNode;
}

function resolveVariant(variant: ButtonProps['variant']): ButtonVariant {
  if (!variant) return 'primary';
  if (variant in LEGACY_VARIANT_ALIAS) {
    return LEGACY_VARIANT_ALIAS[variant as LegacyButtonVariant];
  }
  return variant as ButtonVariant;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedVariant = resolveVariant(variant);

    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant: resolvedVariant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant: resolvedVariant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
